import { MainPane, PaneHeader, layout, useSegmentedTabsStyles } from '@batiflow/ui';
import { Tab, TabList, __css } from '@fluentui/react-components';
import { useRouter } from '@tanstack/react-router';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { PAGE_LIMIT, SearchPanel, type SearchEntry } from '@/features/search';
import { OrganisationFiles } from '../components/organisation-files';
import { RecentFiles } from '../components/recent-files';
import { SharedWithMe } from '../components/shared-with-me';
import { FILES_TABS, isFilesTab, type FilesSearch, type FilesTab } from '../search';

const useStyles = __css({"tabs":{"Bnt446c":0,"B1hrpyx":0,"Fjva8n":"f1xzk6ru","Bhzhn95":0,"B0czu1p":0,"cglito":"f10fimcf","Bnnss6s":"fi64zpg","Dbcxam":0,"rjzwhg":0,"Bblux5w":"f1vjz9w0","B68tc82":"f1oy3dpc","Bmxbyg5":"f1sil6mw"},"content":{"B7ck84d":"f1ewtqcl","mc9l5x":"f22iagw","Beiy3e4":"f1vx9l62","Belr9w4":"f1k5ttpu","Dbcxam":0,"rjzwhg":0,"Bblux5w":"fqo2xn4","Bnt446c":0,"B1hrpyx":0,"Fjva8n":"f1xzk6ru","Bfkbjar":0,"bduzmn":0,"laph2b":"f1plhb1x","Bhzhn95":0,"B0czu1p":0,"cglito":"f10fimcf"},"search":{"D305fb":"f1bjjmyr"}});

export interface FilesPageProps {
  orgId: string;
  /** The open tab, from the address (`undefined` = recent). */
  tab: FilesSearch['tab'];
  onTabChange: (tab: FilesSearch['tab']) => void;
}

/**
 * The Files app of the rail (GED-FE-09, docs/plan/12-frontend.md): recent files, the
 * organisation's own folders, the spaces other organisations share with the person, and a
 * document search. Every list comes from the API, which only returns what the person may open.
 */
export function FilesPage({ orgId, tab, onTabChange }: FilesPageProps) {
  const { t } = useTranslation('documents');
  const { t: tUi } = useTranslation('ui');
  const styles = useStyles();
  const segmented = useSegmentedTabsStyles();
  const active: FilesTab = tab ?? 'recent';

  return (
    <MainPane
      header={<PaneHeader title={tUi('shell.apps.files')} />}
      tabs={
        <div className={styles.tabs}>
          <TabList
            className={segmented.list}
            selectedValue={active}
            aria-label={t('filesApp.tabsLabel')}
            onTabSelect={(_event, data) => {
              const next = String(data.value);
              if (isFilesTab(next)) onTabChange(next === 'recent' ? undefined : next);
            }}
          >
            {FILES_TABS.map((value) => (
              <Tab key={value} value={value} className={segmented.tab}>
                {t(`filesApp.tabs.${value}`)}
              </Tab>
            ))}
          </TabList>
        </div>
      }
    >
      <div className={styles.content}>
        {active === 'recent' ? <RecentFiles orgId={orgId} /> : null}
        {active === 'organisation' ? <OrganisationFiles orgId={orgId} /> : null}
        {active === 'shared' ? <SharedWithMe /> : null}
        {active === 'search' ? <FilesSearchTab orgId={orgId} /> : null}
      </div>
    </MainPane>
  );
}

/** The global search, limited to documents (folders the person reads, GED-BE-08). */
function FilesSearchTab({ orgId }: { orgId: string }) {
  const { t } = useTranslation('documents');
  const styles = useStyles();
  const router = useRouter();
  const [input, setInput] = useState('');
  return (
    <div className={styles.search}>
      <SearchPanel
        orgId={orgId}
        input={input}
        onInputChange={setInput}
        scope="documents"
        limit={PAGE_LIMIT}
        perGroup={PAGE_LIMIT}
        seeMore={false}
        focusOnMount
        fieldLabel={t('filesApp.search.inputLabel')}
        placeholder={t('filesApp.search.placeholder')}
        onOpen={(entry: SearchEntry) => {
          router.history.push(entry.href);
        }}
        onSubmit={() => undefined}
      />
    </div>
  );
}


import "/opt/batiflow/app/apps/web/src/features/files/pages/files-page.griffel.css";